/**
 * useLeftNavigation
 *
 * LeftNavigation and Header from Gravity are tightly coupled and must reside
 * in the same component. Because of this, the visibility logic for LeftNavigation
 * lives here in the main-nav-app rather than being handled externally.
 *
 * Additionally, the single-spa layout elements rendered by other micro-frontends
 * (e.g. @gartner/profile, @gartner/footer) are not children of Header's DOM tree,
 * so CSS scoping via a parent selector is not possible. This hook toggles a class
 * on document.body to allow app.scss to conditionally apply layout offset styles
 * to those sibling single-spa containers only when LeftNavigation is active.
 */
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Constants } from "@constants/Constants";

const {
  LEFT_NAVIGATION_BODY_CLASS,
  COMMON: { END_USER_ROLE },
  EXCLUDED_PATHS_FOR_LEFT_NAV: EXCLUDED_PATHS,
} = Constants;

const computeAvailability = (role, pathname) =>
  END_USER_ROLE.includes(role) &&
  !EXCLUDED_PATHS.some((path) => pathname.includes(path));

export default function useLeftNavigation(role) {
  const { pathname } = useLocation();

  const [isLeftNavigationAvailable, setIsLeftNavigationAvailable] = useState(
    () => computeAvailability(role, pathname)
  );

  useEffect(() => {
    const available = computeAvailability(role, pathname);
    setIsLeftNavigationAvailable(available);

    if (available) {
      document.body.classList.add(LEFT_NAVIGATION_BODY_CLASS);
    } else {
      document.body.classList.remove(LEFT_NAVIGATION_BODY_CLASS);
    }

    return () => {
      document.body.classList.remove(LEFT_NAVIGATION_BODY_CLASS);
    };
  }, [role, pathname]);

  return { isLeftNavigationAvailable };
}
